import { useEffect, useState } from 'react';
import { Loading } from 'components/NudosComponents';
import GenericSearchBar from 'components/genericSearchBar/GenericSearchBar';
import useProductsInOrderState from 'state/useProductsInOrderState';
import useDebounce from 'hooks/useDebounce';
import { IMPNInfo, IproductItemData } from 'types/orders';
import { createNewMPN, getMPNs } from 'services/orders';
import { PRODUCT_BRANDS } from 'types/products';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';

interface IMPNInputProps {
  handleDataChanged: (value: boolean) => void;
  useSameMPN: boolean;
  productItem: IproductItemData;
  productBrand: string;
  countryId?: number | null;
  countryCode?: string;
  updateItemWithinTheList?: (product: IproductItemData) => void;
  isFirstItem: boolean;
}

const MPNInput = ({
  handleDataChanged,
  useSameMPN,
  productItem,
  productBrand,
  countryId,
  countryCode,
  updateItemWithinTheList,
  isFirstItem
}: IMPNInputProps) => {
  const { setAreRequiredNPMInputsFilled } = useProductsInOrderState();

  const [searchMPNString, setSearchMPNString] = useState('');
  const [openMPNList, setOpenMPNList] = useState(false);
  const [loadingMPN, setLoadingMPN] = useState(true);
  const [mpnList, setMPNList] = useState<IMPNInfo[]>();

  const searchMPNFilter = useDebounce(searchMPNString, 500);
  const requireMPN = productBrand.toLowerCase() === PRODUCT_BRANDS.APPLE.toLowerCase();

  const handleChangeMPN = (newMPN: string, mpnId: number) => {
    changeMPN(newMPN, mpnId);
    setSearchMPNString(newMPN);
    setOpenMPNList(false);
    handleDataChanged(true);
    if (productItem.saleId && requireMPN) {
      setAreRequiredNPMInputsFilled(+productItem.saleId, !!newMPN);
    }
  };

  const searchMPN = async () => {
    if (!searchMPNFilter || searchMPNFilter.length < 2) return;
    setLoadingMPN(true);
    try {
      const response = await getMPNs(productBrand, searchMPNFilter);
      if (response) {
        setMPNList(response);
        setLoadingMPN(false);
      }
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al buscar MPNs</>
      });
      setLoadingMPN(false);
    }
  };

  const createMPN = async () => {
    if (!countryId || !countryCode)
      return displayErrorNotification({
        customJSXMessage: <>No se pudo crear MPN, falta información del país</>
      });
    const body = { mpn: searchMPNString, brand: productBrand };
    setLoadingMPN(true);
    try {
      const response = await createNewMPN(body);
      const { mpn, id } = response.data;
      if (mpn && id) handleChangeMPN(mpn, id);
      setOpenMPNList(false);
      setLoadingMPN(false);
      handleDataChanged(true);
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al crear MPN</>
      });
      setLoadingMPN(false);
    }
  };

  const newMPNOption = (
    <div key={`${productItem.saleId}-newMPNOption`} className="newMPNOption" onClick={createMPN}>
      <div className="searchedText">{searchMPNString} - </div>
      <div className="newText"> Nuevo</div>
    </div>
  );

  const formMPNOptionsUI = () => {
    if (!mpnList || mpnList.length === 0) return undefined;
    const mpnOptionsJSXElements = mpnList.map((mpnItem, i) => {
      return (
        <div
          key={`mpn-${mpnItem.id}-${i}`}
          className="option-container"
          onClick={() => handleChangeMPN(mpnItem.mpn, mpnItem.id)}
        >
          <p>{mpnItem.mpn}</p>
        </div>
      );
    });
    return mpnOptionsJSXElements;
  };

  const changeMPN = (newMPN: string, mpnId: number) => {
    updateItemWithinTheList?.({ ...productItem, mpn: newMPN, mpnId: mpnId });
  };

  useEffect(() => {
    if (searchMPNFilter) searchMPN();
  }, [searchMPNFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!requireMPN || !productItem.saleId) return;
    setAreRequiredNPMInputsFilled(+productItem.saleId, !!productItem?.mpn);
  }, [productBrand, productItem.saleId, productItem?.mpn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchMPNString(productItem?.mpn || '');
  }, [productItem?.mpn, productItem]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="inputContainer">
      <div className="title">MPN{requireMPN ? '*' : ''}</div>
      <div className={`mpn ${useSameMPN && !isFirstItem ? 'disabled' : 'active'}`}>
        <GenericSearchBar
          searchString={searchMPNString}
          setSearchString={setSearchMPNString}
          setIsSelectOpen={searchMPNString.length > 1 ? setOpenMPNList : () => setOpenMPNList(false)}
          searchedOptions={formMPNOptionsUI()}
          isSelectOpen={openMPNList}
          initialValue={productItem?.mpn || undefined}
          handleChangeSelectedOption={(newMPN: string) => handleChangeMPN(newMPN, 0)}
          noResultsMessage={loadingMPN ? <Loading key="loading" /> : newMPNOption}
          isSearchDisabled={useSameMPN && !isFirstItem}
        />
      </div>
    </div>
  );
};

export default MPNInput;
